  import { Combobox } from 'baseui/combobox';
import { useSnackbar } from 'baseui/snackbar';
import { Spinner } from 'baseui/spinner';
import parseDuration from 'parse-duration';
import prettyMilliseconds from 'pretty-ms';
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { DialogTrigger } from 'react-aria-components';
import { Check, Plus, Trash } from 'react-feather';
import { Control, FieldError } from 'react-hook-form';
import { z } from 'zod';

import { FormControl } from 'components/FormControl';
import { FormController } from 'components/FormController';
import { Input, InputProps } from 'components/Input';
import { Select } from 'components/Select';
import { createTableBuilder } from 'components/Table';
import { AlertDialog } from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import { DialogActions, DialogBody, DialogTitle } from 'components/ui/dialog';
import { LineItem } from 'services/api/work-requests/types';
import {
  useWorkTypeByIdQuery,
  useWorkTypesQuery,
} from 'services/api/work-type/endpoints';
import { WorkType } from 'services/api/work-type/types';
import { priceFormatter } from 'utils/helpers';
import useDebounce from 'utils/hooks/useDebounce';
import { useLocale } from 'utils/hooks/useLocale';

const { Table, Column } = createTableBuilder<LineItem & { index: number }>();
const toNumber = (maybeNumber: unknown) =>
  z.coerce.number().catch(0).default(0).parse(maybeNumber);

type TaxableColumnProps = {
  row: LineItem & { index: number };
  onUpdate: (index: number, args: LineItem) => void;
  hasRowError: (
    index: number,
  ) => Record<keyof LineItem, FieldError> | undefined;
};

type LineItemProps = {
  errors?: Record<keyof LineItem, FieldError>[];
  control: Control<any>;
  items: LineItem[];
  workRequestId: number;
  onAdd: (args: LineItem) => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, args: LineItem) => void;
};

const useWorkTypeDetails = (workTypeId: number | null | undefined) => {
  const { data: workType } = useWorkTypeByIdQuery(workTypeId ?? 0, {
    skip: workTypeId == null,
  });

  return workType?.quickbooksId || null;
};

export default function LineItems({
  control,
  errors = [],
  items,
  workRequestId,
  onAdd: onAddHandler,
  onRemove: onRemoveHandler,
  onUpdate: onUpdateHandler,
}: LineItemProps) {
  const comboboxRef = useRef<HTMLInputElement>(null);

  const [itemTypes, setItemTypes] = useState<{
    [key: number]: 'SERVICE' | 'INVENTORY';
  }>({});

  const handleNewItem = () => {
    /**
     * TODO:
     * think of better we to describe the new line item as `new`
     */
    onAddHandler({
      id: 0,
      workRequestId,
      description: '',
      duration: '',
      isTaxable: false,
      name: '',
      quantity: 1,
      totalPrice: 0,
      unitPrice: 0,
      actualDuration: null,
      actualQuantity: null,
      actualTotalPrice: null,
      type: 'SERVICE',
      workTypeId: 0,
    });

    // We applied queueMicrotask here because React wouldn't treat the ComboBox
    // as a default input, this queueMicrotask will focus on the ComboBox every Add Line Item
    queueMicrotask(() => {
      if (comboboxRef.current) {
        comboboxRef.current.focus();
      }
    });
  };

  const { t } = useLocale();

  const getRowError = (rowIndex: number) =>
    errors.find((error, index) => rowIndex === index);
  const hasRowError = (index: number) => getRowError(index);

  // TODO: Organize useStates

  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({});
  const [durationValues, setDurationValues] = useState<{
    [key: number]: string;
  }>({});
  const [unitPriceValues, setUnitPriceValues] = useState<{
    [key: number]: number;
  }>({});

  const [forceComboboxClear, setForceComboboxClear] = useState(false);
  const [localItems, setLocalItems] = useState<LineItem[]>(items);

  const [quickbooksIds, setQuickbooksIds] = useState<{
    [key: number]: string | null;
  }>({});

  const [savedInputs, setSavedInputs] = useState<{
    [key: number]: {
      SERVICE: Partial<LineItem>;
      INVENTORY: Partial<LineItem>;
    };
  }>({});

  const debouncedValues = useDebounce(inputValues, 1000);
  const debouncedDurationValues = useDebounce(durationValues, 1000);
  const debouncedUnitPriceValues = useDebounce(unitPriceValues, 1000);

  const { enqueue } = useSnackbar();

  const handleComboboxChange = (rowIndex: number) => (value: string) => {
    onUpdateHandler(rowIndex, {
      ...items[rowIndex],
      name: value,
      workTypeId: null,
    });
  };

  const handleComboboxSelect = (rowIndex: number) => (selected: WorkType) => {
    const formattedDuration = selected.estimatedDuration
      ? prettyMilliseconds(parseDuration(selected.estimatedDuration))
      : '';

    onUpdateHandler(rowIndex, {
      ...items[rowIndex],
      name: selected.name,
      description: selected.description ?? '',
      duration: formattedDuration,
      unitPrice: selected.price,
      totalPrice: selected.price,
      isTaxable: selected.isTaxable,
      workTypeId: selected.id,
    });

    setQuickbooksIds((prev) => ({
      ...prev,
      [rowIndex]: selected.quickbooksId || null,
    }));
  };

  const handleInputChange = (
    index: number,
    value: string,
    row: any,
    field: any,
  ) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [index]: value,
    }));
    field.onChange(value);

    setSavedInputs((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [itemTypes[index] || localItems[index].type]: {
          ...prev[index]?.[itemTypes[index] || localItems[index].type],
          description: value,
        },
      },
    }));
  };

  const handleDurationChange = (
    index: number,
    value: string,
    row: any,
    field: any,
  ) => {
    const newValue = value.trim() === '' ? '0' : value;
    setDurationValues((prevValues) => ({
      ...prevValues,
      [index]: newValue,
    }));
    field.onChange(newValue);
  };

  const handleTypeChange = (index: number, value: 'INVENTORY' | 'SERVICE') => {
    setItemTypes((prev) => ({
      ...prev,
      [index]: value,
    }));

    if (localItems[index]) {
      setSavedInputs((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          [localItems[index].type]: {
            name: localItems[index].name,
            description: localItems[index].description,
            duration: localItems[index].duration,
            quantity: localItems[index].quantity,
            unitPrice: localItems[index].unitPrice,
            totalPrice: localItems[index].totalPrice,
            isTaxable: localItems[index].isTaxable,
            workTypeId: localItems[index].workTypeId,
          },
        },
      }));

      let newItem: LineItem;

      if (savedInputs[index]?.[value]) {
        newItem = {
          ...localItems[index],
          type: value,
          ...savedInputs[index][value],
        };
      } else {
        newItem = {
          ...localItems[index],
          type: value,
          name: '',
          description: '',
          duration: '',
          quantity: 1,
          unitPrice: 0,
          totalPrice: 0,
          isTaxable: false,
          workTypeId: null,
        };
      }

      onUpdateHandler(index, newItem);

      const updatedItems = [...localItems];
      updatedItems[index] = newItem;
      setLocalItems(updatedItems);

      setInputValues((prev) => ({ ...prev, [index]: newItem.description }));
      setDurationValues((prev) => ({ ...prev, [index]: newItem.duration }));
      setUnitPriceValues((prev) => ({ ...prev, [index]: newItem.unitPrice }));

      if (comboboxRef.current) {
        comboboxRef.current.value = '';
      }

      setForceComboboxClear((prev) => !prev);
    }
  };

  const handleUnitPriceChange = (
    index: number,
    value: number,
    row: any,
    field: any,
  ) => {
    setUnitPriceValues((prevValues) => ({
      ...prevValues,
      [index]: value,
    }));
    field.value = value;
    field.onChange(field.value);
  };

  // Debounce for Description
  useEffect(() => {
    Object.entries(debouncedValues).forEach(([index, debouncedValue]) => {
      const rowIndex = Number(index);
      onUpdateHandler(rowIndex, {
        ...items[rowIndex],
        description: debouncedValue,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValues]);

  // Debounce for Duration
  useEffect(() => {
    Object.entries(debouncedDurationValues).forEach(
      ([index, debouncedValue]) => {
        const rowIndex = Number(index);
        onUpdateHandler(rowIndex, {
          ...items[rowIndex],
          duration: debouncedValue,
        });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDurationValues]);

  // Debounce for Unit Price
  useEffect(() => {
    Object.entries(debouncedUnitPriceValues).forEach(
      ([index, debouncedValue]) => {
        const rowIndex = Number(index);
        const row = items[rowIndex];

        // Only call onUpdateHandler after debounce
        onUpdateHandler(rowIndex, {
          ...row,
          unitPrice: debouncedValue,
          totalPrice: row.quantity * toNumber(debouncedValue),
        });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUnitPriceValues]);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  return (
    <div className="w-full space-y-4">
      <Table
        key={items.length}
        data={items.map((item, index) => ({ ...item, index, key: item.id }))}
        emptyMessage={
          <div className="flex h-full w-full flex-col items-center justify-center space-y-4">
            <span className="text-lg font-semibold text-gray-500">
              No items found
            </span>
          </div>
        }
        overrides={{
          TableBodyCell: {
            props: {
              className: 'px-2',
            },
          },
        }}
      >
        <Column header={t('Item Type')}>
          {(row) => (
            <div className="min-w-[10rem] max-w-[10rem]">
              <FormController
                // key={
                //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //   // @ts-ignore
                //   row['item-id']
                // }
                control={control}
                name={`items.${row.index}.type` as const}
                caption={
                  hasRowError(row.index) && (
                    <span aria-hidden className="invisible">
                      No Error
                    </span>
                  )
                }
              >
                {({ field }) => (
                  <Select
                    id={`item-type-${row.index}`}
                    kind="secondary"
                    value={
                      itemTypes[row.index]
                        ? [
                            {
                              id: itemTypes[row.index],
                              label: ['INVENTORY', 'SERVICE'].includes(
                                itemTypes[row.index],
                              )
                                ? itemTypes[row.index] === 'INVENTORY'
                                  ? 'Inventory'
                                  : 'Service'
                                : 'Unknown',
                            },
                          ]
                        : field.value
                        ? [
                            {
                              id: field.value,
                              label: ['INVENTORY', 'SERVICE'].includes(
                                field.value,
                              )
                                ? field.value === 'INVENTORY'
                                  ? 'Inventory'
                                  : 'Service'
                                : 'Unknown',
                            },
                          ]
                        : undefined
                    }
                    onChange={({ option }) => {
                      const selectedValue = option?.id as
                        | 'INVENTORY'
                        | 'SERVICE';
                      handleTypeChange(row.index, selectedValue);
                    }}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    options={[
                      { id: 'INVENTORY', label: t('INVENTORY') },
                      { id: 'SERVICE', label: t('SERVICE') },
                    ]}
                    clearable={false}
                    disabled={row.index === 0}
                  />
                )}
              </FormController>
            </div>
          )}
        </Column>
        <Column header={t('Item')}>
          {(row) => {
            return (
              <div className="min-w-[12rem] max-w-[12rem]">
                <FormController
                  control={control}
                  name={`items.${row.index}.name` as const}
                  caption={
                    hasRowError(row.index) && (
                      <span aria-hidden className="invisible">
                        No Error
                      </span>
                    )
                  }
                >
                  {({ field }) => (
                    <WorkTypeSearchCombobox
                      ref={comboboxRef}
                      key={`${row.index}-${forceComboboxClear}`}
                      value={localItems[row.index]?.name ?? ''}
                      onChange={(value) => {
                        field.onChange(value);
                        handleComboboxChange(row.index)(value);
                        setSavedInputs((prev) => ({
                          ...prev,
                          [row.index]: {
                            ...prev[row.index],
                            [itemTypes[row.index] ||
                            localItems[row.index].type]: {
                              ...prev[row.index]?.[
                                itemTypes[row.index] ||
                                  localItems[row.index].type
                              ],
                              name: value,
                            },
                          },
                        }));
                      }}
                      onSelect={(selected) => {
                        handleComboboxSelect(row.index)(selected);
                        setSavedInputs((prev) => ({
                          ...prev,
                          [row.index]: {
                            ...prev[row.index],
                            [itemTypes[row.index] ||
                            localItems[row.index].type]: {
                              ...prev[row.index]?.[
                                itemTypes[row.index] ||
                                  localItems[row.index].type
                              ],
                              name: selected.name,
                              description: selected.description,
                              duration: selected.estimatedDuration,
                              unitPrice: selected.price,
                              isTaxable: selected.isTaxable,
                              workTypeId: selected.id,
                            },
                          },
                        }));
                      }}
                      itemType={itemTypes[row.index] || items[row.index]?.type}
                    />
                  )}
                </FormController>
              </div>
            );
          }}
        </Column>
        <Column header={t('Description')}>
          {(row) => (
            <div className="min-w-[9rem] max-w-[9rem]">
              <FormController
                key={
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  row['item-id']
                }
                control={control}
                name={`items.${row.index}.description` as const}
                caption={
                  hasRowError(row.index) && (
                    <span aria-hidden className="invisible">
                      No Error
                    </span>
                  )
                }
              >
                {({ field }) => (
                  <input
                    {...field}
                    value={field.value || ''}
                    onChange={(e) =>
                      handleInputChange(row.index, e.target.value, row, field)
                    }
                    className="form-input block w-full rounded-md border-0 px-4 py-2.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary-600"
                  />
                )}
              </FormController>
            </div>
          )}
        </Column>
        <Column header={`${t('Duration')}`}>
          {(row) => (
            <FormController
              key={
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                row['item-id']
              }
              control={control}
              name={`items.${row.index}.duration` as const}
              caption={
                hasRowError(row.index) && (
                  <span aria-hidden className="invisible">
                    No Error
                  </span>
                )
              }
            >
              {({ field }) => (
                <input
                  {...field}
                  value={field.value || '0'}
                  onChange={(e) =>
                    handleDurationChange(row.index, e.target.value, row, field)
                  }
                  className="form-input block w-full rounded-md border-0 px-4 py-2.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary-600"
                />
              )}
            </FormController>
          )}
        </Column>
        <Column header={t('Quantity')}>
          {(row) => (
            <FormController
              control={control}
              name={`items.${row.index}.quantity` as const}
              caption={
                hasRowError(row.index) && (
                  <span aria-hidden className="invisible">
                    No Error
                  </span>
                )
              }
            >
              {({ field }) => (
                <input
                  {...field}
                  value={localItems[row.index]?.quantity ?? 1}
                  onChange={(e) => {
                    field.value = e.target.value as unknown as number;
                    field.onChange(field.value);
                    onUpdateHandler(row.index, {
                      ...row,
                      quantity: Number(field.value),
                      totalPrice: row.unitPrice * Number(field.value),
                    });
                  }}
                  className="form-input block w-full rounded-md border-0 px-4 py-2.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary-600"
                />
              )}
            </FormController>
          )}
        </Column>
        <Column header={t('Unit Price')}>
          {(row) => (
            <FormController
              key={
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                row['item-id']
              }
              control={control}
              name={`items.${row.index}.unitPrice` as const}
              caption={
                hasRowError(row.index) && (
                  <span aria-hidden className="invisible">
                    No Error
                  </span>
                )
              }
            >
              {({ field }) => (
                <input
                  {...field}
                  value={field.value}
                  onChange={(e) =>
                    handleUnitPriceChange(
                      row.index,
                      e.target.value as unknown as number,
                      row,
                      field,
                    )
                  }
                  className="form-input block w-full rounded-md border-0 px-4 py-2.5 text-base text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-primary-600"
                />
              )}
            </FormController>
          )}
        </Column>
        <Column header={t('Amount')}>
          {(row) => (
            <FormControl
              /**
               * To prevent layout shifts when one of the fields
               * has an validation error
               */
              caption={
                hasRowError(row.index) && (
                  <span aria-hidden className="invisible">
                    No Error
                  </span>
                )
              }
            >
              <div className="w-full text-center">
                {priceFormatter().format(
                  row.quantity * toNumber(row.unitPrice),
                )}
              </div>
            </FormControl>
          )}
        </Column>
        <Column header={t('Taxable')}>
          {(row) => (
            <TaxableColumn
              row={row}
              onUpdate={onUpdateHandler}
              hasRowError={hasRowError}
            />
          )}
        </Column>
        <Column
          header={t('Action')}
          overrides={{
            TableBodyCell: {
              props: {
                className: 'relative',
              },
            },
          }}
        >
          {(row) =>
            row.index !== 0 && (
              <div className="absolute left-0 top-0 p-4">
                <div className="inline-flex w-full items-start justify-center pt-1">
                  <DialogTrigger>
                    <Button type="button" aria-label="Remove item" color="red">
                      <Trash data-slot="icon" />
                    </Button>
                    <AlertDialog>
                      {({ state }) => (
                        <>
                          <DialogTitle>Delete Item</DialogTitle>
                          <DialogBody>
                            <div>
                              Are you sure you want to delete this item?
                            </div>
                          </DialogBody>
                          <DialogActions>
                            <Button
                              type="button"
                              variant="plain"
                              onClick={() => {
                                state.close();
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              aria-label="Remove item"
                              color="red"
                              onClick={() => {
                                onRemoveHandler(row.index);
                                state.close();
                                enqueue({
                                  message: t(
                                    'estimates_or_jobs_line_items_delete_successfully',
                                  ),
                                  startEnhancer: ({ size }) => (
                                    <Check size={size} />
                                  ),
                                });
                              }}
                            >
                              Delete
                            </Button>
                          </DialogActions>
                        </>
                      )}
                    </AlertDialog>
                  </DialogTrigger>
                </div>
              </div>
            )
          }
        </Column>
      </Table>
      <Button
        type="button"
        className="w-full"
        size="lg"
        startEnhancer={<Plus />}
        onClick={handleNewItem}
      >
        {t('Add Item')}
      </Button>
    </div>
  );
}

function TaxableColumn({ row, onUpdate, hasRowError }: TaxableColumnProps) {
  const { t } = useLocale();
  const rowError = hasRowError(row.index);
  const quickbooksId = useWorkTypeDetails(row.workTypeId);

  return (
    <FormControl
      caption={
        rowError && (
          <span aria-hidden className="invisible">
            No Error
          </span>
        )
      }
    >
      <Select
        kind="primary"
        value={[{ id: row.isTaxable ? 1 : 0 }]}
        onChange={({ option }) => {
          onUpdate(row.index, {
            ...row,
            isTaxable: Boolean(option?.id),
          });
        }}
        getOptionLabel={({ option }) =>
          option.label ? t(option.label.toString()) : ''
        }
        getValueLabel={({ option }) =>
          option.label ? t(option.label.toString()) : ''
        }
        options={[
          {
            id: 1,
            label: 'Yes',
          },
          {
            id: 0,
            label: 'No',
          },
        ]}
        clearable={false}
        disabled={!!quickbooksId}
      />
    </FormControl>
  );
}

type WorkSearchType = 'name' | 'search' | 'type';

type WorkTypeResultOption = WorkType & {
  searchQuery: string;
  searchType: WorkSearchType;
};

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function ReplacementNode(props: {
  isSelected: boolean;
  option: WorkTypeResultOption;
}) {
  const { option } = props;

  const higlightMatched = (
    value: string,
    matcher: WorkTypeResultOption['searchType'],
  ) => {
    if (matcher !== option.searchType) return value;
    const escapedQuery = escapeRegExp(option.searchQuery);
    const regex = new RegExp(escapedQuery, 'gi');
    if (!regex.test(value)) return value;
    return value.replace(
      regex,
      (matched) =>
        `<span class="text-brand-primary font-bold">${matched}</span>`,
    );
  };
  return (
    <div className="px-3 py-2">
      <div className="space-x-1 text-base font-semibold text-gray-400">
        <span
          dangerouslySetInnerHTML={{
            __html: higlightMatched(option.name, 'name'),
          }}
        />
        <div className="space-y-1 font-prompt  text-gray-500 ">
          <span
            dangerouslySetInnerHTML={{
              __html: higlightMatched(option.type, 'type'),
            }}
          />
        </div>
      </div>
    </div>
  );
}

type WorkTypeSearchComboboxProps = {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (selected: WorkType) => void;
  searchType?: WorkSearchType;
  itemType?: 'SERVICE' | 'INVENTORY';
};

const WorkTypeSearchCombobox = forwardRef<
  HTMLInputElement,
  Omit<InputProps, keyof WorkTypeSearchComboboxProps> &
    WorkTypeSearchComboboxProps
>((props, ref) => {
  const { t } = useLocale();
  const {
    value: controlledValue,
    onChange: handleChange,
    onSelect: handleSelected,
    searchType = 'name',
    itemType,
    ...inputProps
  } = props;

  const [localValue, setLocalValue] = useState(controlledValue || '');
  const debouncedSearch = useDebounce(localValue);
  const workTypeQuery = useWorkTypesQuery(
    {
      params: {
        [searchType]: debouncedSearch || '',
        type: itemType,
        limit: 2000,
      },
    },
    { skip: false },
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const filteredOptions = workTypeQuery.data
    ? workTypeQuery.data.list.filter((item) => item.type === itemType)
    : [];

  useEffect(() => {
    if (controlledValue !== localValue) {
      setLocalValue(controlledValue || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledValue]);

  return (
    <>
      <Combobox<WorkTypeResultOption>
        value={localValue}
        onChange={(nextValue, selectedValue) => {
          if (selectedValue) {
            setLocalValue(selectedValue.name);
            if (handleSelected) {
              handleSelected(selectedValue);
            }
          } else {
            setLocalValue(nextValue);
            if (handleChange) {
              handleChange(nextValue);
            }
          }
        }}
        options={filteredOptions.map((item) => ({
          ...item,
          searchQuery: localValue,
          searchType,
        }))}
        mapOptionToString={(option) => `${option.name}`}
        mapOptionToNode={workTypeQuery.isSuccess ? ReplacementNode : undefined}
        name="input-overrides"
        overrides={{
          Input: {
            component: Input,
            props: {
              ...inputProps,
              inputRef,
              endEnhancer: workTypeQuery.isFetching ? (
                <Spinner $size={16} $borderWidth={2} />
              ) : undefined,
            } as InputProps,
          },
          Root: { props: { className: 'w-full' } },
          ListItem: { props: { className: 'h-auto' } },
          Popover: {
            props: {
              // `flip`'s default boundary is the viewport, but
              // `preventOverflow`'s default boundary is the nearest
              // scrollable ancestor (the Job Items table's own scroll
              // container). When the table is short (few rows), that
              // scroll container's content height is small, so
              // `preventOverflow` "corrects" the dropdown back on top of
              // the field even though the actual browser viewport has
              // plenty of room below. Aligning both to the viewport
              // makes the decision match what's really on screen.
              popperOptions: {
                modifiers: {
                  flip: {
                    enabled: true,
                    boundariesElement: 'viewport',
                  },
                  preventOverflow: {
                    enabled: true,
                    padding: 0,
                    boundariesElement: 'viewport',
                  },
                },
              },
            },
          },
        }}
      />
    </>
  );
});

WorkTypeSearchCombobox.displayName = 'WorkTypeSearchCombobox';
